import React from 'react';
import { BookOpen } from 'lucide-react';
import { CurriculumTabConfig, CurriculumTab } from '../config/curriculumTabs';
import { StudentDropdown } from './StudentDropdown';

interface CurriculumHeaderProps {
  tabs?: CurriculumTabConfig[];
  activeTab?: CurriculumTab;
  onTabChange?: (tab: CurriculumTab) => void;
  showTabs?: boolean;
  children?: React.ReactNode;
  showStudentDropdown?: boolean;
  title?: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
}

export function CurriculumHeader({ tabs, activeTab, onTabChange, showTabs = true, children, showStudentDropdown = true, title = 'Library', icon: Icon = BookOpen }: CurriculumHeaderProps) {
  const handleTabClick = (tab: CurriculumTabConfig) => {
    if (tab.href) {
      // Navigate to the href
      window.location.hash = tab.href;
    } else if (onTabChange) {
      // Use callback for tab change
      onTabChange(tab.id);
    }
  };

  // Show "Curriculum Library" on tablet/desktop, "Library" on mobile
  const displayTitle = title === 'Library' ? (
    <>
      <span className="sm:hidden">Library</span>
      <span className="hidden sm:inline">Curriculum Library</span>
    </>
  ) : title;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Icon className="mr-3 text-action-blue" size={24} />
          <h1 className="text-2xl font-bold text-gray-900">{displayTitle}</h1>
        </div>

        {showStudentDropdown && <StudentDropdown />}
      </div>

      {/* Top Navigation Tabs */}
      {showTabs && tabs && tabs.length > 0 && (
        <div className="mb-6">
          <nav className="flex space-x-0 border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-action-blue text-action-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {children}
    </>
  );
}
